<template>
  <div class="pageRoot linkListPage">
    <div ref="pageCard" class="pageShell">
      <div ref="pageCardMain" class="pageShell__main">
        <div
          ref="scrollArea"
          class="pageShell__scroll"
          :class="{ 'pageShell__scroll--drawerOpen': filterDrawerOpen }"
        >
          <div
            class="listWorkspace"
            :class="{ 'listWorkspace--scrolled': controlBarScrolled }"
          >
            <section
              ref="filterAnchor"
              class="filterCard filterAnchor"
              :class="{ 'filterAnchor--drawerOpen': filterDrawerOpen }"
            >
              <ai-role-review-list-filter-panel
                v-bind="filterPanelProps"
                v-on="filterPanelListeners"
              />
            </section>

            <section
              ref="tableZone"
              class="tableCard tableZone"
              :class="{ 'tableCard--scrolled': controlBarScrolled }"
            >
              <div
                class="scrollControlBar"
                :class="{ 'scrollControlBar--scrolled': controlBarScrolled }"
              >
                <div class="scrollControlBar__lead">
                  <span class="scrollControlBar__label">共</span>
                  <strong class="scrollControlBar__count">{{ listTotalCount }}</strong>
                  <span class="scrollControlBar__label">条</span>
                </div>

                <div
                  v-if="!controlBarScrolled"
                  class="scrollControlBar__stage scrollControlBar__stage--full"
                >
                  <control-bar-display
                    layout="info"
                    :scope-filters="activeScopeFilters"
                    :has-active-scope-filters="hasActiveScopeFilters"
                    :scope-filter-tooltip="scopeFilterTooltip"
                    :selection-count="0"
                    @open-filter="openFilterDrawer"
                  />
                </div>

                <template v-else>
                  <div class="scrollControlBar__stage scrollControlBar__stage--left">
                    <control-bar-display
                      part="info"
                      layout="info"
                      :scope-filters="activeScopeFilters"
                      :has-active-scope-filters="hasActiveScopeFilters"
                      :scope-filter-tooltip="scopeFilterTooltip"
                      :selection-count="0"
                      @open-filter="openFilterDrawer"
                    />
                  </div>
                  <div class="scrollControlBar__navSlot">
                    <control-bar-nav
                      @scroll-top="scrollToTableTop"
                      @open-filter="openFilterDrawer"
                    />
                  </div>
                  <div class="scrollControlBar__stage scrollControlBar__stage--right" />
                </template>

                <div class="scrollControlBar__page">
                  <template v-if="listTotalCount > 0">
                    <span>第 {{ listRangeStart }}–{{ listRangeEnd }} 条</span>
                    <span class="scrollControlBar__metaSep" aria-hidden="true">·</span>
                    <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
                  </template>
                  <template v-else>
                    <span class="scrollControlBar__emptyHint">暂无数据</span>
                    <span class="scrollControlBar__metaSep" aria-hidden="true">·</span>
                    <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
                  </template>
                </div>
              </div>

              <div v-if="!loading && tableData.length" class="tableSection">
                <el-table
                  ref="multipleTable"
                  class="linkTable linkTable--hybrid"
                  :data="tableData"
                  tooltip-effect="dark"
                  style="width: 100%"
                  row-key="id"
                >
                  <el-table-column
                    label="名称"
                    prop="name"
                    align="center"
                    header-align="center"
                    min-width="140"
                    show-overflow-tooltip
                  />
                  <el-table-column
                    label="uid"
                    prop="uid"
                    align="center"
                    header-align="center"
                    min-width="120"
                    show-overflow-tooltip
                  />
                  <el-table-column
                    label="头像"
                    align="center"
                    header-align="center"
                    width="110"
                  >
                    <template slot-scope="scope">
                      <img
                        v-lazy="scope.row.thumbUrl"
                        alt="thumbUrl"
                        class="tableIcon"
                      />
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="角色类型"
                    align="center"
                    header-align="center"
                    width="110"
                  >
                    <template slot-scope="scope">
                      <span
                        class="typeTag"
                        :class="
                          scope.row.uid ? 'typeTag--warning' : 'typeTag--success'
                        "
                      >
                        {{ scope.row.uid ? 'UGC' : 'PGC' }}
                      </span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="状态"
                    align="center"
                    header-align="center"
                    width="130"
                  >
                    <template slot-scope="scope">
                      <span
                        class="statusPill"
                        :class="
                          'statusPill--' + statusPillVariant(scope.row.reviewStatus)
                        "
                      >
                        <span class="statusPill__dot" aria-hidden="true" />
                        {{ statusLabel(scope.row.reviewStatus) }}
                      </span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="更新时间"
                    prop="updateTime"
                    align="center"
                    header-align="center"
                    min-width="160"
                    show-overflow-tooltip
                  />
                  <el-table-column
                    label="操作"
                    label-class-name="linkListActionColumnHead"
                    align="center"
                    header-align="center"
                    width="120"
                    class-name="actionColumn"
                  >
                    <template slot="header">
                      <span class="linkListActionColumnHead__label">操作</span>
                    </template>
                    <template slot-scope="scope">
                      <div class="linkTableActionCell">
                        <el-button
                          size="small"
                          class="rowActionBtn rowActionBtn--ghost"
                          icon="el-icon-view"
                          @click.stop="goInfo(scope.row.id)"
                        >
                          查看
                        </el-button>
                      </div>
                    </template>
                  </el-table-column>
                </el-table>
              </div>

              <div v-else-if="!loading && !tableData.length" class="tableEmpty">
                <div class="tableEmpty__icon"><i class="el-icon-user" /></div>
                <p class="tableEmpty__title">暂无 AI 角色审核记录</p>
                <p class="tableEmpty__desc">调整筛选条件后重新查询。</p>
              </div>

              <div v-else class="tableSkeletonWrap">
                <el-skeleton :rows="8" animated />
              </div>
            </section>
          </div>
        </div>

        <div
          v-if="filterDrawerOpen"
          class="filterDrawerLayer"
          role="dialog"
          aria-modal="true"
          aria-label="筛选与操作"
        >
          <div
            class="filterDrawerPanel"
            :class="{ 'filterDrawerPanel--scrollable': drawerPanelScrollable }"
            :style="drawerPanelStyle"
          >
            <div class="filterDrawerPanel__header">
              <span class="filterDrawerPanel__title">筛选与操作</span>
              <div class="filterDrawerPanel__headerActions">
                <span v-if="activeFilterCount" class="filterDrawerPanel__chip">
                  已筛选 {{ activeFilterCount }} 项
                </span>
                <button
                  type="button"
                  class="filterDrawerPanel__closeBtn"
                  @click="closeFilterDrawer"
                >
                  收起
                </button>
              </div>
            </div>
            <div ref="drawerScroll" class="filterDrawerPanel__scroll">
              <ai-role-review-list-filter-panel
                v-bind="{ ...filterPanelProps, showPanelSummary: true }"
                v-on="filterPanelListeners"
              />
            </div>
          </div>
          <div
            class="filterDrawerBackdrop"
            aria-hidden="true"
            @click="closeFilterDrawer"
            @wheel.prevent
          />
        </div>
      </div>

      <div class="pageShell__footer tableCard__footer footerBar">
        <div class="footerSummary">
          共 <strong class="footerSummary__count">{{ listTotalCount }}</strong> 条 · 第
          {{ currentPage }} / {{ totalPages }} 页
        </div>
        <el-pagination
          class="linkPagination"
          layout="prev, pager, next, sizes, jumper"
          :total="listTotalCount"
          :page-sizes="[25, 50, 100]"
          :page-size="currentPageSize"
          :current-page.sync="currentPage"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { getAiRoleReviewList } from '../../../api/aiRoleReview'
import {
  roleReviewStatusLabel,
  roleReviewStatusPillVariant,
} from '../aiAuditStatus'
import AiRoleReviewListFilterPanel from './AiRoleReviewListFilterPanel.vue'
import ControlBarDisplay from '../../resourceManage/genericResourceManage/ControlBarDisplay.vue'
import ControlBarNav from '../../resourceManage/genericResourceManage/ControlBarNav.vue'
import linkListScrollControlMixin from '../../resourceManage/genericResourceManage/linkListScrollControlMixin'

export default {
  name: 'AiRoleReviewList',
  components: {
    AiRoleReviewListFilterPanel,
    ControlBarDisplay,
    ControlBarNav,
  },
  mixins: [linkListScrollControlMixin],
  data() {
    return {
      name: '',
      uid: '',
      roleType: '',
      tableData: [],
      currentPage: 1,
      currentPageSize: 25,
      total: 0,
      loading: false,
    }
  },
  computed: {
    listTotalCount() {
      const parsed = Number(this.total)
      return Number.isFinite(parsed) ? parsed : 0
    },
    listRangeStart() {
      if (this.listTotalCount === 0) return 0
      return (this.currentPage - 1) * this.currentPageSize + 1
    },
    listRangeEnd() {
      if (this.listTotalCount === 0) return 0
      return Math.min(this.currentPage * this.currentPageSize, this.listTotalCount)
    },
    totalPages() {
      if (this.listTotalCount === 0) return 1
      return Math.max(1, Math.ceil(this.listTotalCount / this.currentPageSize))
    },
    activeFilterCount() {
      return [this.name, this.uid, this.roleType].filter(
        (value) => value !== '' && value !== null && value !== undefined
      ).length
    },
    activeScopeFilters() {
      const chips = []
      if (String(this.name || '').trim()) {
        chips.push({ key: 'name', label: '名称', value: this.name.trim() })
      }
      if (String(this.uid || '').trim()) {
        chips.push({ key: 'uid', label: '用户 id', value: this.uid.trim() })
      }
      if (this.roleType !== '' && this.roleType != null) {
        chips.push({
          key: 'roleType',
          label: '角色类型',
          value: Number(this.roleType) === 1 ? 'PGC' : 'UGC',
        })
      }
      return chips
    },
    hasActiveScopeFilters() {
      return this.activeScopeFilters.length > 0
    },
    scopeFilterTooltip() {
      return this.activeScopeFilters
        .map((item) => `${item.label}: ${item.value}`)
        .join(' · ')
    },
    filterPanelProps() {
      return {
        name: this.name,
        uid: this.uid,
        roleType: this.roleType,
        activeFilterCount: this.activeFilterCount,
        panelTotalCount: this.listTotalCount,
      }
    },
    filterPanelListeners() {
      return {
        search: this.search,
        'reset-filters': this.resetFilters,
        'reset-and-search': this.resetAndSearch,
        'update:name': (value) => {
          this.name = value
        },
        'update:uid': (value) => {
          this.uid = value
        },
        'update:roleType': (value) => {
          this.roleType = value
          this.search()
        },
      }
    },
  },
  watch: {
    loading(value) {
      if (!value) {
        this.onLinkListDataRendered()
      } else {
        this.$nextTick(() => {
          this.measureTableZoneWidth()
        })
      }
    },
    tableData() {
      this.$nextTick(() => {
        this.remeasureFilterBlockHeight()
        this.updateScrollControls()
        this.scheduleTableLayout()
      })
    },
    filterDrawerOpen(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          this.syncDrawerLayout()
        })
      }
    },
  },
  created() {
    this.bindLinkListScrollControl()
    this.getList()
  },
  methods: {
    statusLabel: roleReviewStatusLabel,
    statusPillVariant: roleReviewStatusPillVariant,
    getList() {
      this.loading = true
      getAiRoleReviewList({
        pageNum: this.currentPage,
        pageSize: this.currentPageSize,
        name: this.name,
        uid: this.uid,
        roleType: this.roleType,
      })
        .then((res) => {
          if (res.success) {
            this.total = res.total
            this.tableData = res.data || []
          }
        })
        .catch((err) => {
          console.log(err)
        })
        .finally(() => {
          this.loading = false
          this.$nextTick(() => {
            this.measureTableZoneWidth()
            this.scheduleTableLayout()
          })
        })
    },
    search() {
      this.currentPage = 1
      this.getList()
    },
    resetFilters() {
      this.name = ''
      this.uid = ''
      this.roleType = ''
    },
    resetAndSearch() {
      this.resetFilters()
      this.search()
    },
    handleSizeChange(val) {
      this.currentPageSize = val
      this.$nextTick(() => {
        this.getList()
      })
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this.getList()
    },
    goInfo(id) {
      window.open(`/#/tools/aiRoleReview/info/${id}`, '_blank')
    },
  },
}
</script>

<style scoped>
.tableIcon {
  object-fit: cover;
  height: 80px;
  box-shadow: 0 3px 5px 0 rgba(0, 0, 0, 0.2);
}
</style>
